import React, { useState, useEffect } from 'react';
import { ChevronRight, Clock, Search, Syringe, Stethoscope, Wrench, XCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { getCustomers, getPets, getClinicOperations, getAvailableSlots, getAppointmentSettings, createDirectBooking } from '../../services/api';
import { toast } from 'sonner';
import PetAvatar from '../PetAvatar';

const TYPE_CONFIG = {
  vaccine: { label: 'Aşılar', icon: Syringe, color: 'text-red-600', bg: 'bg-red-50' },
  treatment: { label: 'Tedaviler', icon: Stethoscope, color: 'text-blue-600', bg: 'bg-blue-50' },
  service: { label: 'Hizmetler', icon: Wrench, color: 'text-emerald-600', bg: 'bg-emerald-50' },
};

const DirectBookingModal = ({ open, onOpenChange, onCreated }) => {
  const [step, setStep] = useState(1);
  const [search, setSearch] = useState('');
  const [customers, setCustomers] = useState([]);
  const [pets, setPets] = useState([]);
  const [operations, setOperations] = useState([]);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [clinicSettings, setClinicSettings] = useState(null);

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedPet, setSelectedPet] = useState(null);
  const [selectedOp, setSelectedOp] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (open) {
      setStep(1);
      setSearch('');
      setSelectedCustomer(null);
      setSelectedPet(null);
      setSelectedOp(null);
      setSelectedDate('');
      setNote('');
      loadCustomers();
      loadOperations();
      loadClinicSettings();
    }
  }, [open]);

  const loadCustomers = async () => {
    try { setCustomers((await getCustomers()).data); } catch (e) { console.error(e); }
  };
  const loadOperations = async () => {
    try { setOperations((await getClinicOperations()).data); } catch (e) { console.error(e); }
  };
  const loadClinicSettings = async () => {
    try { setClinicSettings((await getAppointmentSettings()).data); } catch (e) { console.error(e); }
  };

  const selectCustomer = async (c) => {
    setSelectedCustomer(c);
    try { setPets((await getPets(c.id)).data); } catch (e) { console.error(e); }
    setStep(2);
  };
  const selectPet = (p) => { setSelectedPet(p); setStep(3); };
  const selectOp = (op) => { setSelectedOp(op); setStep(4); };

  const selectDate = async (date) => {
    setSelectedDate(date);
    setSlotsLoading(true);
    try {
      const res = await getAvailableSlots(date, selectedOp?.id);
      setSlots(res.data.slots || []);
      if (res.data.is_closed) toast.info('Bu gün klinik kapalı');
    } catch (e) { toast.error('Slotlar yüklenemedi'); }
    finally { setSlotsLoading(false); }
    setStep(5);
  };

  const selectSlot = async (slot) => {
    setSubmitting(true);
    try {
      await createDirectBooking({
        customer_id: selectedCustomer.id, pet_id: selectedPet.id,
        operation_id: selectedOp?.id, operation_name: selectedOp?.name || 'Genel Muayene',
        date: selectedDate, start_time: slot.time, note,
      });
      toast.success('Randevu oluşturuldu');
      onOpenChange(false);
      onCreated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Randevu oluşturulamadı');
    } finally { setSubmitting(false); }
  };

  // Only show customers matching search (empty search = empty list)
  const filteredCustomers = search.trim().length >= 2
    ? customers.filter(c =>
        c.name.toLowerCase().includes(search.toLowerCase()) || c.phone.includes(search)
      )
    : [];

  // Group operations by type
  const groupedOps = operations.reduce((acc, op) => {
    const type = op.type || 'service';
    if (!acc[type]) acc[type] = [];
    acc[type].push(op);
    return acc;
  }, {});

  // Date options with closed day info
  const getDateOptions = () => {
    const closedDays = new Set();
    if (clinicSettings?.working_hours) {
      clinicSettings.working_hours.forEach(wh => {
        if (wh.is_closed) closedDays.add(wh.day);
      });
    }
    const dates = [];
    const today = new Date();
    for (let i = 0; i < 30; i++) {
      const d = new Date(today);
      d.setDate(d.getDate() + i);
      const dow = (d.getDay() + 6) % 7; // JS Sunday=0 -> Monday=0
      dates.push({
        value: d.toISOString().split('T')[0],
        label: d.toLocaleDateString('tr-TR', { weekday: 'short', day: 'numeric', month: 'long' }),
        isToday: i === 0,
        isClosed: closedDays.has(dow),
      });
    }
    return dates;
  };

  const stepTitles = { 1: 'Müşteri Seçin', 2: 'Evcil Hayvan Seçin', 3: 'İşlem Seçin', 4: 'Tarih Seçin', 5: 'Saat Seçin' };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:rounded-2xl sm:max-w-md lg:max-w-3xl sm:max-h-[75vh] overflow-hidden flex flex-col p-0">
        {/* Fixed header */}
        <div className="px-5 pt-5 pb-3 border-b border-gray-100">
          <DialogHeader>
            <DialogTitle className="text-base">{stepTitles[step]}</DialogTitle>
          </DialogHeader>
          <div className="flex items-center gap-1.5 mt-3">
            {[1, 2, 3, 4, 5].map(s => (
              <div key={s} className={`h-1 flex-1 rounded-full transition-colors ${s <= step ? 'bg-[#6b6de3]' : 'bg-gray-200'}`} />
            ))}
          </div>
        </div>

        {/* Scrollable content */}
        <div className="px-5 pb-5 pt-3 overflow-y-auto max-h-[calc(85vh-90px)]">
          {/* Step 1: Customer */}
          {step === 1 && (
            <div className="space-y-3">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8E93]" />
                <Input value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Müşteri Ara (İsim, telefon veya TCKN)" autoFocus
                  className="h-10 pl-8 text-sm bg-[#F2F2F7] border-0 rounded-xl" data-testid="customer-search-input" />
              </div>
              {search.trim().length < 2 ? (
                <p className="text-center text-[#8E8E93] py-6 text-xs">En az 2 karakter yazarak müşteri arayın</p>
              ) : filteredCustomers.length === 0 ? (
                <p className="text-center text-[#8E8E93] py-6 text-xs">Eşleşen müşteri bulunamadı</p>
              ) : (
                <div className="space-y-1.5 max-h-[320px] overflow-y-auto">
                  {filteredCustomers.map(c => (
                    <button key={c.id} onClick={() => selectCustomer(c)}
                      className="w-full flex items-center justify-between p-3 rounded-xl bg-[#F2F2F7] hover:bg-gray-200 transition-colors text-left"
                      data-testid={`select-customer-${c.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 text-sm">{c.name}</p>
                        <p className="text-xs text-[#8E8E93]">{c.phone}</p>
                        {c.pets && c.pets.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {c.pets.map(p => (
                              <span key={p.id} className="inline-flex items-center gap-0.5 text-[10px] bg-white px-1.5 py-0.5 rounded-md text-gray-600">
                                <PetAvatar species={p.species} size={10} /> {p.name}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <ChevronRight size={16} className="text-[#8E8E93] shrink-0 ml-2" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 2: Pet */}
          {step === 2 && (
            <div className="space-y-2">
              <button onClick={() => { setStep(1); setSearch(''); }} className="text-xs text-[#6b6de3] mb-1 flex items-center gap-1">
                <ChevronRight size={12} className="rotate-180" /> {selectedCustomer?.name}
              </button>
              {pets.map(p => (
                <button key={p.id} onClick={() => selectPet(p)}
                  className="w-full flex items-center gap-3 p-3 rounded-xl bg-[#F2F2F7] hover:bg-gray-200 transition-colors text-left"
                  data-testid={`select-pet-${p.id}`}>
                  <PetAvatar species={p.species} gender={p.gender} size={22} />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 text-sm">{p.name}</p>
                    <p className="text-xs text-[#8E8E93]">{p.species === 'dog' ? 'Köpek' : p.species === 'cat' ? 'Kedi' : p.species}</p>
                  </div>
                  <ChevronRight size={16} className="text-[#8E8E93]" />
                </button>
              ))}
              {pets.length === 0 && <p className="text-center text-[#8E8E93] py-4 text-sm">Pet bulunamadı</p>}
            </div>
          )}

          {/* Step 3: Operation (grouped) */}
          {step === 3 && (
            <div className="space-y-4">
              <button onClick={() => setStep(2)} className="text-xs text-[#6b6de3] mb-1 flex items-center gap-1">
                <ChevronRight size={12} className="rotate-180" /> {selectedPet?.name}
              </button>
              {['vaccine', 'treatment', 'service'].map(type => {
                const ops = groupedOps[type];
                if (!ops || ops.length === 0) return null;
                const cfg = TYPE_CONFIG[type];
                const Icon = cfg.icon;
                return (
                  <div key={type}>
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-6 h-6 ${cfg.bg} rounded-md flex items-center justify-center`}>
                        <Icon size={13} className={cfg.color} />
                      </div>
                      <span className="text-xs font-semibold text-gray-700">{cfg.label}</span>
                      <span className="text-[10px] text-[#8E8E93]">({ops.length})</span>
                    </div>
                    <div className="space-y-1.5">
                      {ops.map(op => (
                        <button key={op.id} onClick={() => selectOp(op)}
                          className="w-full flex items-center justify-between p-3 rounded-xl bg-[#F2F2F7] hover:bg-gray-200 transition-colors text-left"
                          data-testid={`select-op-${op.id}`}>
                          <div>
                            <p className="font-medium text-gray-900 text-sm">{op.name}</p>
                            {op.duration_minutes && <span className="text-[10px] text-[#8E8E93]">{op.duration_minutes} dk</span>}
                          </div>
                          <ChevronRight size={16} className="text-[#8E8E93]" />
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Step 4: Date */}
          {step === 4 && (
            <div className="space-y-2">
              <button onClick={() => setStep(3)} className="text-xs text-[#6b6de3] mb-1 flex items-center gap-1">
                <ChevronRight size={12} className="rotate-180" /> {selectedOp?.name}
              </button>
              <Input value={note} onChange={e => setNote(e.target.value)} placeholder="Not (opsiyonel)"
                className="h-9 text-sm bg-[#F2F2F7] border-0 rounded-xl mb-2" data-testid="booking-note-input" />
              <div className="max-h-[300px] overflow-y-auto space-y-1">
                {getDateOptions().map(d => (
                  d.isClosed ? (
                    <div key={d.value}
                      className="w-full flex items-center justify-between p-3 rounded-xl bg-red-50/60 opacity-60 cursor-not-allowed"
                      data-testid={`select-date-${d.value}`}>
                      <span className="text-sm text-gray-400 capitalize">{d.label}</span>
                      <span className="text-[10px] bg-red-100 text-red-500 px-2 py-0.5 rounded-full font-medium">Kapalı</span>
                    </div>
                  ) : (
                    <button key={d.value} onClick={() => selectDate(d.value)}
                      className="w-full flex items-center justify-between p-3 rounded-xl bg-[#F2F2F7] hover:bg-gray-200 transition-colors text-left"
                      data-testid={`select-date-${d.value}`}>
                      <span className="text-sm text-gray-900 capitalize">{d.label}</span>
                      {d.isToday && <span className="text-[10px] bg-[#6b6de3] text-white px-2 py-0.5 rounded-full">Bugün</span>}
                    </button>
                  )
                ))}
              </div>
            </div>
          )}

          {/* Step 5: Time Slot */}
          {step === 5 && (
            <div className="space-y-2">
              <button onClick={() => setStep(4)} className="text-xs text-[#6b6de3] mb-1 flex items-center gap-1">
                <ChevronRight size={12} className="rotate-180" /> Geri
              </button>
              <div className="p-3 bg-[#F2F2F7] rounded-xl mb-2">
                <p className="text-xs text-[#8E8E93]">
                  <strong>{selectedCustomer?.name}</strong> &bull; {selectedPet?.name} &bull; {selectedOp?.name} &bull; {new Date(selectedDate + 'T00:00:00').toLocaleDateString('tr-TR', { day: 'numeric', month: 'long' })}
                </p>
              </div>
              {slotsLoading ? (
                <p className="text-center text-[#8E8E93] py-4 text-sm">Yükleniyor...</p>
              ) : slots.length > 0 ? (
                <div className="grid grid-cols-3 gap-2 max-h-[300px] overflow-y-auto">
                  {slots.map(slot => (
                    <button key={slot.time} onClick={() => selectSlot(slot)} disabled={submitting}
                      className="p-3 rounded-xl bg-[#F2F2F7] hover:bg-[#6b6de3] hover:text-white transition-colors text-center group"
                      data-testid={`select-slot-${slot.time}`}>
                      <p className="text-sm font-medium">{slot.time}</p>
                      <p className="text-[10px] text-[#8E8E93] group-hover:text-white/70">{slot.end_time}</p>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <Clock size={36} className="text-[#C7C7CC] mx-auto mb-2" />
                  <p className="text-[#8E8E93] text-sm">Bu tarihte müsait saat yok</p>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DirectBookingModal;
